import { RequireAccess } from '@/components/require-access'
import { useJobillaOrgId } from '@/hooks'
import { useAuth, useEntitlements } from '@/lib'
import { logger, SettingsSubheading } from '@/ui'
import { ConfirmationDialog, ConfirmationDialogRef } from '@/ui/rapid-ui/structural/confirmation-dialog'
import { Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions } from '@headlessui/react'
import {
	BuildingOfficeIcon,
	ChevronDownIcon,
	ChevronRightIcon,
	LinkIcon,
	PlusIcon,
	XMarkIcon,
} from '@heroicons/react/24/outline'
import {
	organisationIndexQueryKey,
	type OrganisationRelationData,
	organisationRelationShowQueryKey,
	useOrganisationIndex,
	useOrganisationOwner,
	useOrganisationRelationLinkChild,
	useOrganisationRelationShow,
	useOrganisationRelationUnlinkChild,
	useOrganisationRemoveOwner,
} from '@jobilla/nexus-sdk'
import { useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

type CompanyRelationOption = Pick<OrganisationRelationData, 'id' | 'name'>

export function CompanyRelations() {
	const { t } = useTranslation('common')
	const organisationId = useJobillaOrgId()

	const relationQuery = useOrganisationRelationShow(organisationId, { enabled: !!organisationId })

	useEffect(() => {
		if (!relationQuery.isError) {
			return
		}

		logger.error(relationQuery.error, {
			category: 'api',
			tags: {
				feature: 'settings-company-relations',
				action: 'load-relations',
			},
			data: { organisationId },
		})
	}, [organisationId, relationQuery.error, relationQuery.isError])

	const relation = relationQuery.data
	const parentOrganization = relation?.parent ?? null

	return (
		<div className="space-y-8 mt-8">
			<section>
				<SettingsSubheading>{t('companyRelations.currentOrganization')}</SettingsSubheading>
				<div className="mt-4">
					<CurrentOrganisationCard
						relation={relation}
						loading={relationQuery.isLoading || relationQuery.isFetching}
					/>
				</div>
			</section>

			{parentOrganization
				? <ParentCompanySection parent={parentOrganization} />
				: <ChildCompaniesManager />}
		</div>
	)
}

function CurrentOrganisationCard({ relation, loading }: {
	relation: OrganisationRelationData | undefined
	loading: boolean
}) {
	const { t } = useTranslation('common')

	if (loading) {
		return (
			<div className="p-4 bg-grey-50 rounded-lg text-grey-500">
				{t('companyRelations.loadingOrg')}
			</div>
		)
	}

	if (!relation) {
		return (
			<div className="p-4 bg-grey-50 rounded-lg text-grey-500">
				{t('companyRelations.noOrganizationFound')}
			</div>
		)
	}

	const ownerValue = relation.meta?.owner
	const currentOwner = typeof ownerValue === 'string' ? ownerValue : null

	return (
		<div className="flex items-center gap-3 p-4 bg-grey-50 rounded-lg">
			<BuildingOfficeIcon className="w-5 h-5 text-grey-600" />
			<div>
				<div className="font-medium text-grey-800">{relation.name}</div>
				<div className="text-sm text-grey-500">ID: {relation.id}</div>
				<RequireAccess permission="organisation:owner">
					<OwnerControl currentOwner={currentOwner} />
				</RequireAccess>
			</div>
		</div>
	)
}

function OwnerControl({ currentOwner }: { currentOwner: string | null }) {
	const { t } = useTranslation('common')
	const organisationId = useJobillaOrgId()
	const queryClient = useQueryClient()

	const refetchRelations = async () => {
		await queryClient.invalidateQueries({
			queryKey: organisationRelationShowQueryKey(organisationId),
		})
	}

	const assignOwner = useOrganisationOwner({
		mutationKey: ['organisation-assign-owner'],
		onSuccess: refetchRelations,
	})
	const removeOwner = useOrganisationRemoveOwner({
		mutationKey: ['organisation-remove-owner'],
		onSuccess: refetchRelations,
	})

	return (
		<div className="text-sm text-grey-500">
			{t('companyRelations.owner')}: {currentOwner
				? (
					<span className="inline-flex items-center gap-2">
						{currentOwner}
						{removeOwner.isPending
							? <span>{t('companyRelations.saving')}</span>
							: (
								<button
									type="button"
									onClick={() => removeOwner.mutate({ organisation: organisationId })}
									className="underline cursor-pointer"
								>
									{t('companyRelations.remove')}
								</button>
							)}
					</span>
				)
				: assignOwner.isPending
				? <>{t('companyRelations.saving')}</>
				: (
					<button
						type="button"
						onClick={() => assignOwner.mutate({ organisation: organisationId })}
						className="underline cursor-pointer"
					>
						{t('companyRelations.assignMyself')}
					</button>
				)}
		</div>
	)
}

function ParentCompanySection({ parent }: { parent: OrganisationRelationData }) {
	const { t } = useTranslation('common')
	// A child shows what it inherited (its parent's entitlements).
	const inheritedFeatures = parent.entitlements ?? []

	return (
		// A child must always see who its parent is and what it inherits — this is
		// informational, not the paid management feature, so it is intentionally not gated.
		<section>
			<SettingsSubheading>{t('companyRelations.parentOrganization')}</SettingsSubheading>
			<div className="mt-4 space-y-4">
				<ParentCompanyLink parent={parent} />
				<InheritedFeatureList features={inheritedFeatures} />
			</div>
		</section>
	)
}

function ParentCompanyLink({ parent }: { parent: OrganisationRelationData }) {
	const { t } = useTranslation('common')

	const content = (
		<>
			<LinkIcon className="w-5 h-5 text-brand-600" />
			<div className="flex-1 text-brand-800">
				{t('companyRelations.parentInfo', { name: parent.name })}
			</div>
		</>
	)

	if (!parent.slug) {
		return (
			<div className="flex items-center gap-3 p-4 bg-brand-50 rounded-lg border border-brand-200">
				{content}
			</div>
		)
	}

	return (
		<Link
			to="/company/$companyId/settings"
			params={{ companyId: parent.slug }}
			className="flex items-center gap-3 p-4 bg-brand-50 rounded-lg border border-brand-200 hover:bg-brand-100 transition-colors"
		>
			{content}
			<ChevronRightIcon className="w-5 h-5 text-brand-600" />
		</Link>
	)
}

function InheritedFeatureList({ features }: { features: NonNullable<OrganisationRelationData['entitlements']> }) {
	const { t } = useTranslation('common')

	if (features.length === 0) {
		return (
			<div className="p-4 bg-grey-50 rounded-lg text-sm text-grey-500">
				{t('companyRelations.noInheritedFeatures')}
			</div>
		)
	}

	return (
		<div className="p-4 bg-grey-50 rounded-lg">
			<div className="text-sm font-medium text-grey-800">
				{t('companyRelations.inheritedFeaturesHeading')}
			</div>
			<ul className="mt-2 space-y-1">
				{features.map((feature) => (
					<li key={feature} className="text-sm text-grey-600">
						• {t(`companyRelations.features.${feature}`)}
					</li>
				))}
			</ul>
		</div>
	)
}

function ChildCompaniesManager() {
	const { t } = useTranslation('common')
	const dialogRef = useRef<ConfirmationDialogRef>(null)
	const organisationId = useJobillaOrgId()
	const { permissions } = useAuth()
	const queryClient = useQueryClient()
	const { entitlements } = useEntitlements()

	const relationQuery = useOrganisationRelationShow(organisationId, { enabled: !!organisationId })
	const organisationsQuery = useOrganisationIndex(undefined, { enabled: !!organisationId })

	useEffect(() => {
		if (!organisationsQuery.isError) {
			return
		}

		logger.error(organisationsQuery.error, {
			category: 'api',
			tags: {
				feature: 'settings-company-relations',
				action: 'load-organisations',
			},
			data: { organisationId },
		})
	}, [organisationId, organisationsQuery.error, organisationsQuery.isError])

	const invalidateRelations = async () => {
		await Promise.all([
			queryClient.invalidateQueries({ queryKey: organisationRelationShowQueryKey(organisationId) }),
			queryClient.invalidateQueries({ queryKey: organisationIndexQueryKey() }),
		])
	}

	const linkChildMutation = useOrganisationRelationLinkChild({ onSuccess: invalidateRelations })
	const unlinkChildMutation = useOrganisationRelationUnlinkChild({ onSuccess: invalidateRelations })

	const childOrganizations = relationQuery.data?.children ?? []
	const currentChildIds = new Set(childOrganizations.map((child) => child.id))

	// Eligible children: another org the user can see that is a standalone root
	// (no parent, no children of its own) and not already linked here. The server
	// re-enforces the single-level invariant on link.
	const eligibleChildren = (organisationsQuery.data ?? []).filter((organisation) =>
		organisation.id !== organisationId
		&& !organisation.hasParent
		&& !organisation.hasChildren
		&& !currentChildIds.has(organisation.id)
	)

	const loadingRelations = relationQuery.isLoading
		|| relationQuery.isFetching
		|| organisationsQuery.isLoading
		|| organisationsQuery.isFetching
	const busy = linkChildMutation.isPending || unlinkChildMutation.isPending
	const linkError = linkChildMutation.error?.response?.data?.message
		?? linkChildMutation.error?.message
		?? ''
	const unlinkError = unlinkChildMutation?.error?.response?.data?.message
		?? unlinkChildMutation.error?.message
		?? ''
	// Managing children requires admin (update) on this parent org — admins and jobillans
	// both qualify. Also hide the controls when the hierarchy failed to load, else a failed
	// relation query makes a child look like a manageable standalone root.
	const canManage = (permissions?.includes('organisation:update') ?? false) && !relationQuery.isError

	const handleSelectChild = async (childId: string) => {
		const child = eligibleChildren.find((candidate) => candidate.id === childId)

		const message = (
			<>
				{t('companyRelations.linkChildConfirm', { name: child?.name })}
				{entitlements.length > 0
					? (
						<>
							<span className="mt-3 block font-medium text-grey-800">
								{t('companyRelations.linkChildInheritsHeading')}
							</span>
							<span className="mt-1 block">
								{entitlements.map((feature) => (
									<span key={feature} className="block">
										• {t(`companyRelations.features.${feature}`)}
									</span>
								))}
							</span>
						</>
					)
					: (
						<span className="mt-3 block text-grey-500">
							{t('companyRelations.linkChildNoFeatures')}
						</span>
					)}
			</>
		)

		try {
			await dialogRef.current?.confirm({
				title: t('companyRelations.linkChildTitle'),
				message,
				confirmText: t('companyRelations.linkChildConfirmButton'),
			})
		} catch {
			return // cancelled
		}

		try {
			await linkChildMutation.mutateAsync({ organisation: organisationId, data: { childId: childId } })
			logger.track('Company relations: Child linked', { data: { child_id: childId } })
		} catch {
			// surfaced via linkChildMutation.error
		}
	}

	const handleRemoveChild = async (child: CompanyRelationOption) => {
		try {
			await dialogRef.current?.confirm({
				title: t('companyRelations.unlinkChildTitle'),
				message: t('companyRelations.unlinkChildConfirm', { name: child.name }),
				confirmText: t('companyRelations.unlinkChildConfirmButton'),
				destructive: true,
			})
		} catch {
			return // cancelled
		}

		try {
			await unlinkChildMutation.mutateAsync({ organisation: organisationId, child: child.id })
			logger.track('Company relations: Child removed', { data: { child_id: child.id } })
		} catch {
			// surfaced via unlinkChildMutation.error
		}
	}

	return (
		<section>
			<SettingsSubheading>{t('companyRelations.childOrganizations')}</SettingsSubheading>
			<div className="mt-4 space-y-4">
				{(linkError || unlinkError)
					? (
						<div className="p-4 bg-red-50 rounded-lg border border-red-200 text-red-700 text-sm">
							{linkError || unlinkError}
						</div>
					)
					: null}

				{loadingRelations
					? (
						<div className="p-4 bg-grey-50 rounded-lg text-grey-500">
							{t('companyRelations.loadingChildOrgs')}
						</div>
					)
					: childOrganizations.length > 0
					? (
						<ul className="divide-y divide-grey-100 rounded-lg border border-grey-200">
							{childOrganizations.map((childOrganization) => (
								<li
									key={childOrganization.id}
									className="flex items-center justify-between gap-3 p-4"
								>
									<div className="flex items-center gap-3">
										<BuildingOfficeIcon className="w-5 h-5 text-grey-500" />
										<div>
											<div className="font-medium text-grey-800">{childOrganization.name}</div>
											<div className="text-sm text-grey-500">ID: {childOrganization.id}</div>
										</div>
									</div>
									{canManage && (
										<button
											type="button"
											onClick={() => handleRemoveChild(childOrganization)}
											disabled={busy}
											aria-label={t('companyRelations.removeChildAria', {
												name: childOrganization.name,
											})}
											className="p-1 text-grey-500 hover:text-red-600 disabled:opacity-50 cursor-pointer"
										>
											<XMarkIcon className="w-5 h-5" />
										</button>
									)}
								</li>
							))}
						</ul>
					)
					: (
						<div className="p-4 bg-grey-50 rounded-lg text-grey-500">
							{t('companyRelations.noChildOrganizations')}
						</div>
					)}

				{canManage && !loadingRelations && (
					<ChildCompanyPicker
						options={eligibleChildren}
						onSelect={handleSelectChild}
						disabled={busy}
					/>
				)}
			</div>

			<ConfirmationDialog ref={dialogRef} />
		</section>
	)
}

type ChildCompanyPickerProps = {
	options: CompanyRelationOption[]
	onSelect: (childId: string) => void
	disabled: boolean
}

// Searchable picker on the shared Combobox primitive — Escape, arrow-key nav and focus
// management come for free. Filters the pre-vetted eligible list client-side.
function ChildCompanyPicker({ options, onSelect, disabled }: ChildCompanyPickerProps) {
	const { t } = useTranslation('common')
	const [search, setSearch] = useState('')

	const query = search.trim().toLowerCase()
	const filtered = query ? options.filter((option) => option.name.toLowerCase().includes(query)) : options

	return (
		<Combobox<CompanyRelationOption | null>
			immediate
			disabled={disabled}
			value={null}
			onChange={(option) => {
				if (!option) {
					return
				}
				// Let the Combobox close before the confirm dialog opens, else its option list renders over the modal.
				requestAnimationFrame(() => onSelect(option.id))
			}}
			onClose={() => setSearch('')}
		>
			<div className="relative inline-flex items-center rounded-md border border-grey-200 bg-white">
				<PlusIcon className="pointer-events-none absolute left-3 w-4 h-4 text-grey-500" />
				<ComboboxInput
					aria-label={t('companyRelations.addChild')}
					placeholder={t('companyRelations.addChildPlaceholder')}
					displayValue={() => ''}
					onChange={(event) => setSearch(event.target.value)}
					className="py-2 pl-9 pr-8 text-sm text-grey-700 placeholder-grey-400 bg-transparent rounded-md outline-none disabled:opacity-50 disabled:cursor-not-allowed"
				/>
				<ComboboxButton className="absolute inset-y-0 right-0 flex items-center px-2 text-grey-400 cursor-pointer">
					<ChevronDownIcon className="w-4 h-4" />
				</ComboboxButton>
			</div>

			<ComboboxOptions
				anchor="bottom start"
				className="w-[260px] [--anchor-gap:4px] max-h-[300px] overflow-y-auto rounded-lg border border-grey-200 bg-white py-1 shadow-lg z-[9999]"
			>
				{filtered.length === 0
					? (
						<div className="px-3 py-3 text-sm text-grey-400 text-center">
							{t('companyRelations.noEligibleChildren')}
						</div>
					)
					: filtered.map((option) => (
						<ComboboxOption
							key={option.id}
							value={option}
							className="flex items-center gap-2.5 px-3 py-2 text-sm text-grey-700 text-left cursor-pointer data-[focus]:bg-grey-50"
						>
							<span className="w-6 h-6 rounded-full bg-gradient-to-r from-indigo-600 to-brand-600 flex items-center justify-center text-white text-xs font-semibold shrink-0">
								{option.name.charAt(0).toUpperCase()}
							</span>
							<span className="truncate">{option.name}</span>
						</ComboboxOption>
					))}
			</ComboboxOptions>
		</Combobox>
	)
}
